import axios, { AxiosError, AxiosRequestConfig } from "axios"
import { bindActionCreators } from "redux"

import { authApi } from "./endpoints/authApi"
import { rootAction } from "../../redux/root"
import { TAppDispatch } from "../../redux/store"
import { getAccessToken, getFingerPrint, isAccessTokenExpired, saveAccessToken } from "../utils/auth"
import { showErrorNotification } from "../utils/ui"

export const axiosConfigAuth = (headers?: any, options?: any): AxiosRequestConfig => {

    return {
        headers: {
            Token: `${getAccessToken()}`,
            ...headers
        },
        ...options
    }
}

export const axiosRefreshConfig = (options?: any): AxiosRequestConfig => {
    return {
        withCredentials: true,
        ...options
    }
}

export const handleResponseError = (dispatch: TAppDispatch, error: AxiosError<any>, messages?: {[key: number]: string}) => {

    const { authLogout, setAppGlobalError } = bindActionCreators(rootAction, dispatch)

    if (error.response) {

        if (error.response?.status === 401) {
            authLogout(() => {
                showErrorNotification("Сессия истекла. Пожалуйста, войдите снова.")
            })
            return
        } else if (error.response?.status === 500) {
            setAppGlobalError(true)
            return
        }

        // Пытаемся получить сообщение от бэкенда
        const backendMessage = error.response?.data?.message || error.response?.data?.error

        // Если есть сообщение от бэкенда — показываем его
        if (backendMessage) {
            showErrorNotification(backendMessage)
            return
        }

        // Иначе проверяем кастомные сообщения по статусу
        const statusCode = error.response?.status
        if (statusCode && messages && messages[statusCode]) {
            showErrorNotification(messages[statusCode])
            return
        }

        showErrorNotification("Произошла ошибка, повторите попытку позже.")
        return
    } else {
        showErrorNotification("Произошла ошибка, повторите попытку позже.")
        return
    }
}

// Безопасный парсинг JSON: заменяет большие числа (16+ цифр) на строки
// для предотвращения потери точности (ID от Яндекс Недвижимости и др.)
export const safeJsonParse = (data: string): any => {
    if (typeof data !== "string") return data
    try {
        const safe = data.replace(/(:\s*)(\d{16,})(\s*[,}\]])/g, "$1\"$2\"$3")
        return JSON.parse(safe)
    } catch {
        try {
            return JSON.parse(data)
        } catch {
            return data
        }
    }
}

// Устанавливаем безопасный парсинг JSON при загрузке модуля
axios.defaults.transformResponse = [safeJsonParse]

export const initAuthInterceptor = async () => {
    const fingerprint = await getFingerPrint()

    // checking access token before request
    axios.interceptors.request.use(async (config) => {
        const accessToken = getAccessToken()

        if (config.url?.includes("logout")) {
            return config
        }

        if (config.url?.includes("refresh-token")) {
            config.headers.withCredentials = true
            return config
        }

        if (isAccessTokenExpired(accessToken)) {
            await authApi.refreshToken(fingerprint)
                .then((response) => {
                    const newToken = response.data.access_token

                    config.headers.Token = newToken
                    saveAccessToken(newToken)
                })
                .catch(() => {
                    return Promise.reject({
                        response: {
                            status: 401,
                            message: "Unauthorized",
                        }
                    })
                })
        } else {
            config.headers.Token = accessToken
        }

        return config
    }, (error) => {
        return Promise.resolve(error)
    })
}
